import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Youtube, Twitter, Music, Link2, ExternalLink, AlertCircle } from 'lucide-react';
import { parseEmbedUrl } from '@/lib/embedExtensions';

const EMBED_TYPES = [
  { 
    type: 'youtube', 
    name: 'YouTube', 
    icon: Youtube, 
    color: 'text-red-500',
    placeholder: 'https://youtube.com/watch?v=...',
    description: 'Embed a YouTube video',
  },
  { 
    type: 'twitter', 
    name: 'Twitter/X', 
    icon: Twitter, 
    color: 'text-blue-400',
    placeholder: 'https://twitter.com/user/status/...',
    description: 'Embed a tweet',
  },
  { 
    type: 'spotify', 
    name: 'Spotify', 
    icon: Music, 
    color: 'text-green-500',
    placeholder: 'https://open.spotify.com/track/...',
    description: 'Embed a Spotify track, album, or playlist',
  },
  { 
    type: 'link', 
    name: 'Link Preview', 
    icon: Link2, 
    color: 'text-muted-foreground',
    placeholder: 'https://example.com/article',
    description: 'Embed a link preview card',
  },
];

export default function EmbedDialog({ open, onClose, onInsert }) {
  const [url, setUrl] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const handleUrlChange = (value) => {
    setUrl(value);
    setError('');
    
    if (value.trim()) {
      const parsed = parseEmbedUrl(value.trim());
      if (parsed) {
        setPreview(parsed);
        setSelectedType(parsed.type);
      } else {
        setPreview(null);
      }
    } else {
      setPreview(null);
    }
  };

  const handleInsert = () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    const parsed = parseEmbedUrl(url.trim());
    if (!parsed) {
      setError('Could not parse this URL. Please check the format.');
      return;
    }

    onInsert(parsed);
    handleClose();
  };

  const handleClose = () => {
    setUrl('');
    setSelectedType(null);
    setError('');
    setPreview(null);
    onClose();
  };

  const getEmbedTypeInfo = (type) => EMBED_TYPES.find((t) => t.type === type);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="embed-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ExternalLink className="w-5 h-5" />
            Insert Embed
          </DialogTitle>
          <DialogDescription>
            Paste a URL to embed YouTube videos, tweets, Spotify tracks, and more.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* URL Input */}
          <div className="space-y-2">
            <Label htmlFor="embed-url">URL</Label>
            <Input
              id="embed-url"
              value={url}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="Paste a URL here..."
              className="font-mono text-sm"
              data-testid="embed-url-input"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="p-3 bg-secondary/50 rounded-md border border-border animate-fade-in">
              <div className="flex items-center gap-2 mb-2">
                {(() => {
                  const info = getEmbedTypeInfo(preview.type);
                  if (!info) return null;
                  const Icon = info.icon;
                  return (
                    <>
                      <Icon className={`w-4 h-4 ${info.color}`} />
                      <span className="text-sm font-medium">{info.name}</span>
                    </>
                  );
                })()}
              </div>
              <p className="text-xs text-muted-foreground truncate">{url}</p>
              {preview.type === 'youtube' && (
                <p className="text-xs text-muted-foreground mt-1">Video ID: {preview.videoId}</p>
              )}
              {preview.type === 'twitter' && (
                <p className="text-xs text-muted-foreground mt-1">Tweet ID: {preview.tweetId}</p>
              )}
              {preview.type === 'spotify' && (
                <p className="text-xs text-muted-foreground mt-1">
                  {preview.spotifyType}: {preview.spotifyUri}
                </p>
              )}
            </div>
          )}

          {/* Supported Types */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Supported platforms</Label>
            <div className="grid grid-cols-2 gap-2">
              {EMBED_TYPES.map((embedType) => (
                <div
                  key={embedType.type}
                  className={`flex items-center gap-2 p-2 rounded-md text-xs ${
                    selectedType === embedType.type 
                      ? 'bg-primary/10 border border-primary/30' 
                      : 'bg-secondary/30 border border-transparent'
                  }`}
                >
                  <embedType.icon className={`w-4 h-4 ${embedType.color}`} />
                  <span>{embedType.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleInsert} disabled={!preview} data-testid="embed-insert-btn">
            Insert Embed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
